import styled from '@emotion/styled'
import PropTypes from 'prop-types'
import React from 'react'
import tokens from '../../design/tokens'
import { Collapsible } from '../Collapsible'
import { Flex } from '../Flex/Flex'
import { HelpIcon } from '../HelpIcon'
import { IconArrow1Down, IconArrow1Up } from '../SVGIcons'
import { Title, TITLE_VARIANT_M, TITLE_VARIANT_S } from '../Title'
import { Panel, PANEL_SPACING_VARIANT_SECONDARY } from './Panel'

export const PANEL_COLLAPSIBLE_TITLE_VARIANT_M = TITLE_VARIANT_M
export const PANEL_COLLAPSIBLE_TITLE_VARIANT_S = TITLE_VARIANT_S

export const PanelCollapsible = ({
  title,
  titleTooltip,
  titleVariant,
  onTitleClick,
  children,
  childrenAlt,
  className,
  onClick,
  disabled,
  collapsed,
  onChange,
  beforeCollapseChildren,
  'data-mu-test': dataAttributeTest,
  margin
}) => {
  return (
    <Panel
      className={className}
      data-mu-test={dataAttributeTest}
      onClick={onClick}
      spacingVariant={PANEL_SPACING_VARIANT_SECONDARY}
      disabled={disabled}
      margin={margin}
    >
      <StyledTop>
        <StyledTitleContainer>
          <StyledTitle
            variant={titleVariant}
            onClick={onTitleClick}
            $clickable={typeof onTitleClick === 'function'}
          >
            {title}
          </StyledTitle>
          {titleTooltip && <HelpIcon text={titleTooltip} inline={false} />}
        </StyledTitleContainer>
        <Flex gap='8px'>
          {beforeCollapseChildren && (
            <StyledBeforeCollapseChildrenContainer>
              {beforeCollapseChildren}
            </StyledBeforeCollapseChildrenContainer>
          )}
          <StyledDiv>
            <button onClick={onChange}>
              <StyledIconDown collapsed={collapsed} />
              <StyledIconUp collapsed={collapsed} />
            </button>
          </StyledDiv>
        </Flex>
      </StyledTop>
      {children && (
        <StyledCollapsible
          data-mu-test={dataAttributeTest && `${dataAttributeTest}-children`}
          collapsed={collapsed}
        >
          {children}
        </StyledCollapsible>
      )}

      {childrenAlt && (
        <StyledCollapsible
          data-mu-test={dataAttributeTest && `${dataAttributeTest}-children-alt`}
          collapsed={!collapsed}
        >
          {childrenAlt}
        </StyledCollapsible>
      )}
    </Panel>
  )
}

// PROPS
PanelCollapsible.defaultProps = {
  titleVariant: PANEL_COLLAPSIBLE_TITLE_VARIANT_M,
  margin: `0 0 ${tokens.spacing.large} 0`
}

PanelCollapsible.propTypes = {
  title: PropTypes.string,
  titleTooltip: PropTypes.string,
  titleVariant: PropTypes.oneOf([
    PANEL_COLLAPSIBLE_TITLE_VARIANT_M,
    PANEL_COLLAPSIBLE_TITLE_VARIANT_S
  ]),
  onTitleClick: PropTypes.func,
  children: PropTypes.node,
  childrenAlt: PropTypes.node,
  className: PropTypes.string,
  onClick: PropTypes.func,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  collapsed: PropTypes.bool,
  beforeCollapseChildren: PropTypes.node,
  'data-mu-test': PropTypes.string,
  margin: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
}

// STYLE
const StyledTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const StyledTitleContainer = styled.div`
  display: flex;
  align-items: center;
`
const StyledTitle = styled(Title)`
  display: inline-block;
  ${({ $clickable }) => `cursor: ${$clickable ? 'pointer' : 'default'};`}
`

const StyledCollapsible = styled(Collapsible)`
  .e-collapsible__inner {
    margin-top: ${tokens.spacing.small};
  }
`
const StyledIconDown = styled(IconArrow1Down)`
  opacity: ${({ collapsed }) => (collapsed ? 1 : 0)};
  transition-delay: ${({ collapsed }) => (collapsed ? '0.2s' : '0')} !important;
  transform: ${({ collapsed }) =>
    collapsed ? 'translate(-50%, -50%)' : 'translate(-50%, 0)'};
`
const StyledIconUp = styled(IconArrow1Up)`
  top: 45% !important;
  opacity: ${({ collapsed }) => (collapsed ? 0 : 1)};
  transition-delay: ${({ collapsed }) => (collapsed ? '0' : '0.2s')} !important;
  transform: ${({ collapsed }) =>
    collapsed ? 'translate(-50%, -100%)' : 'translate(-50%, -50%)'};
`

const StyledBeforeCollapseChildrenContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`

const StyledDiv = styled.div`
  button {
    height: 40px;
    width: 40px;
    border-radius: 50px;
    background-color: #f5f5f5;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    border: none;
    outline: none;
    position: relative;

    > * {
      content: '';
      display: block;
      color: #7c7c7c;
      width: 14px;
      height: 14px;
      position: absolute;
      top: 50%;
      left: 50%;
      //transform: translate(-50%, -50%);
      background-position: center;
      background-repeat: no-repeat;
      background-size: 14px;
      transition: all 0.2s ease-out;
    }
  }
`
